import { ArrowRight } from 'lucide-react'
import { Link } from '#router'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { usePageTitle } from '@/hooks/use-document-title'
import Footer from './footer'

const values = [
  {
    title: 'Honesty over hype',
    description:
      'We tell you where you actually stand — including when we think coaching is not the right investment for you yet. We would rather lose a sale than waste your time and money.',
  },
  {
    title: 'Practice like it counts',
    description:
      'Interviews are a performance skill. Everything we build — problems, mock interviews, sprints — is designed to mirror the bar real companies hold you to.',
  },
  {
    title: 'Results, not seat time',
    description:
      'Progress is measured against a goal you set with your coach, not hours logged. Every sprint ends with an honest assessment of whether it moved you closer to an offer.',
  },
  {
    title: 'Respect the effort',
    description:
      'Preparing for interviews while working a job is hard. We meet that effort with structure, accountability, and feedback that is specific enough to act on.',
  },
]

interface TeamMember {
  name: string
  role: string
  bio: string
  imageSrc?: string
}

const team: TeamMember[] = [
  {
    name: 'Mike Mroczka',
    role: 'Founder & Coach',
    bio: 'Co-author of Beyond Cracking the Coding Interview. Interview coach who has helped hundreds of engineers land offers at top companies.',
  },
]

function Headshot({ member }: { member: TeamMember }) {
  if (member.imageSrc) {
    return (
      <img
        src={member.imageSrc}
        alt={`${member.name} headshot`}
        className="h-24 w-24 rounded-full object-cover"
      />
    )
  }
  const initials = member.name
    .split(' ')
    .map((part) => part[0])
    .join('')
  return (
    <div
      aria-hidden
      className="bg-primary/10 text-primary flex h-24 w-24 items-center justify-center rounded-full text-2xl font-semibold"
    >
      {initials}
    </div>
  )
}

export default function About() {
  usePageTitle('About Us')

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col">
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <h1 className="mb-4 text-4xl font-bold tracking-tight">
          About <span className="text-[#51B0BC]">Beyond CTCI</span>
        </h1>
        <p className="text-muted-foreground mb-12 max-w-3xl text-lg leading-relaxed">
          Beyond CTCI is the companion platform to{' '}
          <em>Beyond Cracking the Coding Interview</em>, the sequel to the
          best-selling interview prep classic. We combine a practice platform —
          26 technical topics and ~300 curated problems you can write, run, and
          test solutions against — with a 1:1 coaching program for engineers who
          want structured, honest guidance to an offer.
        </p>

        <section className="mb-14">
          <h2 className="mb-6 text-2xl font-semibold">What We Stand For</h2>
          <div className="grid gap-6 sm:grid-cols-2">
            {values.map((value) => (
              <Card key={value.title}>
                <CardHeader>
                  <CardTitle>{value.title}</CardTitle>
                  <CardDescription className="leading-relaxed">
                    {value.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="mb-6 text-2xl font-semibold">The Team</h2>
          <div className="grid gap-6 sm:grid-cols-2">
            {team.map((member) => (
              <div
                key={member.name}
                className="bg-card flex items-start gap-5 rounded-xl border p-6"
              >
                <Headshot member={member} />
                <div>
                  <p className="text-lg font-semibold">{member.name}</p>
                  <p className="text-[#51B0BC] mb-2 text-sm font-medium">
                    {member.role}
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground mt-6 text-sm leading-relaxed">
            The book behind the platform was written by Gayle Laakmann McDowell,
            Mike Mroczka, Aline Lerner, and Nil Mamano — drawing on insights
            from over 100,000 real interviews.
          </p>
        </section>

        <section className="bg-muted/40 rounded-xl border p-8 text-center">
          <h2 className="mb-2 text-2xl font-semibold">
            Curious what working with us looks like?
          </h2>
          <p className="text-muted-foreground mb-6">
            We explain the whole program — sprints, what we do, and what we
            deliberately don&rsquo;t do.
          </p>
          <Button asChild size="lg" className="group text-base">
            <Link href="/how-it-works">
              See How the Program Works
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </Button>
        </section>
      </div>
      <Footer />
    </div>
  )
}
